import { Gavel, ShieldCheck, Clock } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

type BidAuditMeta = {
  audit?: {
    bidder?: {
      name?: string | null;
    };
    organization?: {
      name?: string | null;
    };
  };
};

type BidRow = {
  id: string;
  amount: number;
  status: string;
  source: string;
  bidderUserId: string;
  bidderName: string;
  bidderEmail: string | null;
  bidderOrganizationId: string;
  bidderOrgName: string;
  meta: BidAuditMeta | null;
  createdAt: string;
};

interface AuctionBidHistoryProps {
  bids: BidRow[];
  onAudit: (bid: BidRow) => void;
}

export function AuctionBidHistory({ bids, onAudit }: AuctionBidHistoryProps) {
  const sortedBids = [...bids].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (bids.length === 0) {
    return (
      <Card className="shadow-sm border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Budhistorikk
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-muted-foreground italic text-center py-4">
            Ingen bud ennå
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Gavel className="h-4 w-4 text-primary" />
          Budhistorikk
        </CardTitle>
        <Badge variant="muted" className="text-[10px] font-bold">
          {bids.length} {bids.length === 1 ? 'bud' : 'bud'}
        </Badge>
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {sortedBids.map((bid, i) => (
            <div 
              key={bid.id} 
              className={`rounded-xl border p-3 space-y-1.5 group transition-all hover:bg-muted/10 ${
                i === 0 
                  ? "border-primary/20 bg-primary/5 shadow-sm" 
                  : "border-border/40 bg-muted/5 hover:border-border"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="text-[13px] font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                      {bid.amount.toLocaleString("nb-NO")} kr
                    </p>
                    {i === 0 && (
                      <Badge variant="accent" className="h-4 px-1 text-[8px] font-black uppercase tracking-tighter">Høyest</Badge>
                    )}
                  </div>
                  <p className="text-[10px] font-bold text-muted-foreground truncate uppercase tracking-tight">
                    {bid.bidderOrgName}
                  </p>
                  <p className="text-[9px] text-muted-foreground/80 font-medium truncate mt-0.5">
                    {bid.bidderName}
                  </p>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <span className="text-[9px] font-medium text-muted-foreground whitespace-nowrap tabular-nums">
                    {new Date(bid.createdAt).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <button 
                    onClick={() => onAudit(bid)}
                    className="p-1 rounded-md opacity-20 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all active:scale-90"
                    title="Se Audit logg"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
